// Shared types for the definition-driven form renderer.
// Mirrors the schema of definition/order-form.json.

export type FieldType =
  | 'text'
  | 'email'
  | 'tel'
  | 'number'
  | 'date'
  | 'stars'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'textarea';

export interface Rule {
  field: string; // field id (not wire)
  op: 'is' | 'isNot';
  value: string;
}

export interface ShowWhen {
  mode: 'all' | 'any';
  rules: Rule[];
}

export interface Option {
  value: string;
  label?: string;
  /**
   * Hide this single choice conditionally (e.g. Discard is not offered
   * on print orders, where it confuses customers who just bought prints).
   * Enforced server-side too, so a hidden choice cannot be submitted.
   */
  showWhen?: ShowWhen;
  /** Small gold pill rendered on the option card (e.g. "Most Popular"). */
  badge?: string;
}

export interface FieldItem {
  kind: 'field';
  id: string;
  wire: string;
  type: FieldType;
  label?: string;
  help?: string;
  required?: boolean;
  options?: Option[];
  emit?: 'value' | 'valueLabel';
  allowOther?: boolean;
  confirm?: boolean;
  min?: number;
  max?: number;
  /**
   * `date` fields only: the earliest allowed date, as a number of months
   * before today. Rolls forward with the calendar, so it never goes stale the
   * way a hardcoded date would (12 = our abandonment policy window).
   */
  minMonthsAgo?: number;
  /** `date` fields only: reject dates after today. */
  noFuture?: boolean;
  maxLength?: number;
  rows?: number;
  /** Pre-filled value. Checkbox fields take an ARRAY of pre-ticked values. */
  default?: string | string[];
  placeholder?: string;
  /**
   * Never rendered; the field still evaluates for visibility and its value
   * (usually `default`) still reaches the payload (e.g. a per-store tag).
   */
  hidden?: boolean;
  showWhen?: ShowWhen;
}

/** Conditional success screen: first variant whose rules pass wins. */
export interface SuccessVariant {
  when: ShowWhen;
  title?: string;
  html: string;
}

export interface SectionItem {
  kind: 'section';
  id: string;
  title?: string;
  description?: string;
  showWhen?: ShowWhen;
  items: Item[];
}

export interface DescriptionItem {
  kind: 'description';
  id: string;
  html: string;
  /**
   * How the block renders. 'inline' (default) = always-open text block;
   * 'dialog' = modal bottom-sheet shown when the block becomes visible, closed
   * by its agree button; 'expander' = collapsed disclosure behind a summary
   * row; 'hero' = full-bleed dark welcome screen (conversational flow);
   * 'info' = compact (i) button beside the field, opens the text in a sheet;
   * 'prompt' = modal shown when the customer tries to CONTINUE past the
   * screen, with `actions` as its buttons. A prompt never blocks: every
   * action closes it and carries on.
   */
  presentation?: 'inline' | 'dialog' | 'expander' | 'hero' | 'info' | 'prompt';
  /** Background image URL for presentation:"hero" (served from our own origin). */
  background?: string;
  /** Button label for presentation:"dialog" (default "I Agree"). */
  agreeLabel?: string;
  /** Collapsed header text for presentation:"expander" / label for "info". */
  summary?: string;
  /**
   * Buttons for presentation:"prompt". One carrying setField/value writes
   * that answer before continuing (the "upgrade" button); the rest just
   * continue. First is styled primary.
   */
  actions?: Array<{ label: string; setField?: string; value?: string }>;
  showWhen?: ShowWhen;
}

export type Item = FieldItem | SectionItem | DescriptionItem;

export interface Step {
  id: string;
  title: string;
  items: Item[];
  /**
   * Repeatable step: the customer can answer it more than once (one order
   * carrying several services). Expanded before rendering — see repeat.ts.
   */
  repeat?: { max: number; itemLabel?: string };
}

export interface FormDefinition {
  slug: string;
  title: string;
  subtitle?: string;
  /**
   * Presentation mode. 'wizard' (default) = step pages with grouped fields;
   * 'conversational' = one question per screen with auto-advance.
   */
  flow?: 'wizard' | 'conversational';
  /** Success-screen celebration: 'tick' draws the gold tick + confetti. */
  successAnimation?: string;
  /** Logo mark URL shown on hero screens and in the conversational header. */
  logo?: string;
  /** Label of the final submit button (default "Submit"). */
  submitLabel?: string;
  /** Heading of the success screen (default order-form copy). */
  successTitle?: string;
  /**
   * Show the RPL reference box on the success screen (default true). Forms
   * where the customer never needs to quote the id (applications, enquiries)
   * set this false; the id is still minted and stored server-side for staff.
   */
  showReference?: boolean;
  /**
   * Optional HTML rendered on the success screen under the reference box.
   * When present it REPLACES the order-form-specific blocks (package
   * instruction, what-happens-next, lab addresses).
   */
  successHtml?: string;
  /** Conditional success screens evaluated in order; first match wins. */
  successVariants?: SuccessVariant[];
  /**
   * {fieldId: queryParam}: a valid URL-parameter value pre-fills the field
   * and (in conversational flow) its screen is skipped entirely, unless the
   * field is listed in prefillConfirm.
   */
  prefill?: Record<string, string>;
  /**
   * Prefill field ids whose screen is NOT skipped: shown with the linked
   * answer already selected plus a Continue button, so a slip (e.g. the wrong
   * star tapped in an email) can still be changed.
   */
  prefillConfirm?: string[];
  /**
   * Device-local memory of a returning customer's own details (see
   * deviceMemory.ts). `review` fields collapse into one confirm screen and
   * their individual screens are skipped; `fill` fields are pre-populated in
   * place and still shown normally.
   */
  remember?: { review?: string[]; fill?: string[] };
  steps: Step[];
}

/** Answer state keyed by field id. Checkboxes hold arrays, everything else strings. */
export type Answers = Record<string, string | string[]>;

/** Sentinel stored as the answer of an allowOther field when "Other" is picked. */
export const OTHER = '__other__';
